// Course-level context for an Attune moment — "horizon-bounded": everything the
// learner has already REACHED (prior sections, full text), and only the TITLES
// of what's still ahead. The hard wall at the horizon is deliberate: an Attune
// moment must never surface material the learner hasn't met yet (spoilers break
// the discovery/companion frame, and "don't mention what's next" is too soft a
// constraint to trust an LLM with). Within the horizon it gets the full picture
// and chooses what's relevant by its position + focus.
//
// Pairs with lib/lessonContext.ts (within-lesson before/after/whole). Tokens are
// cheap here — a whole course is ~5k tokens (see the measurement that motivated
// this) — so we feed real text, not a lossy digest, with only an outlier guard.

import { courseService } from '../../services/courseService';
import { slideToText } from '../lessonContext';
import type { Slide, SlideBasedContent } from '../../components/course-builder/slides/slideTypes';

export interface AttuneCourseContext {
  /** Prior sections (before the current lesson) — full readable text. */
  before: string;
  /** Sections still ahead — titles only (spoiler-light; for the Hook's framing). */
  aheadTitles: string;
  /** Human placement, e.g. "This is section 4 of 9 in the course." */
  placement: string;
}

/** Minimal lesson shape the split needs (kept structural for unit-testability). */
export interface CourseLessonLike {
  id: string;
  title: string;
  /** 'HEADING' lessons are chapter dividers (no learner-facing body). */
  type: string;
  content: unknown;
}

const isSlideBased = (c: unknown): c is SlideBasedContent =>
  !!c && typeof c === 'object' && Array.isArray((c as { slides?: unknown }).slides);

function lessonBody(content: unknown): string {
  if (!isSlideBased(content)) return '';
  return (content.slides as Slide[]).map(slideToText).filter(Boolean).join('\n');
}

// ~30k-token guard (courses are usually ~20k chars / ~5k tokens, so this almost
// never trips — it just stops a pathological course from blowing latency/cost).
const MAX_BEFORE_CHARS = 120_000;

/**
 * Pure horizon split over an ordered lesson list: prior lessons rendered in full
 * (chapter HEADINGs as `## markers`), later lessons as titles only, the current
 * lesson excluded (it's covered by within-lesson context). Returns null if the
 * current lesson isn't found.
 */
export function splitCourseHorizon(
  lessons: CourseLessonLike[],
  currentLessonId: string,
): AttuneCourseContext | null {
  const idx = lessons.findIndex((l) => l.id === currentLessonId);
  if (idx < 0) return null;

  const contentLessons = lessons.filter((l) => l.type !== 'HEADING');
  const posInContent = contentLessons.findIndex((l) => l.id === currentLessonId);
  const placement =
    posInContent >= 0 && contentLessons.length > 1
      ? `This is section ${posInContent + 1} of ${contentLessons.length} in the course.`
      : '';

  const beforeParts: string[] = [];
  const aheadParts: string[] = [];
  for (let i = 0; i < lessons.length; i++) {
    if (i === idx) continue; // current lesson → within-lesson context owns it
    const l = lessons[i];
    if (l.type === 'HEADING') {
      (i < idx ? beforeParts : aheadParts).push(`## ${l.title}`);
      continue;
    }
    if (i < idx) {
      const body = lessonBody(l.content);
      beforeParts.push(`### ${l.title}${body ? `\n${body}` : ''}`);
    } else {
      aheadParts.push(`- ${l.title}`);
    }
  }

  let before = beforeParts.join('\n\n').trim();
  if (before.length > MAX_BEFORE_CHARS) {
    before = `…(earlier sections omitted)…\n\n${before.slice(before.length - MAX_BEFORE_CHARS)}`;
  }
  return { before, aheadTitles: aheadParts.join('\n').trim(), placement };
}

/**
 * Fetch the course and build its horizon context relative to the current
 * lesson. Flattens all sections in order (robust to legacy multi-section
 * courses as well as the flat one-section model). Best-effort: returns null on no
 * course, lesson not found, or any error — course context must never break the
 * moment.
 *
 * `content` defaults to 'published' (the student viewer reads the snapshot
 * students actually see). The builder preview passes 'draft': it is a draft
 * surface, and the published mode would drop every not-yet-published lesson —
 * an unpublished course would otherwise preview with no context at all.
 */
export async function buildAttuneCourseContext(
  courseId: string,
  currentLessonId: string,
  opts: { content?: 'draft' | 'published' } = {},
): Promise<AttuneCourseContext | null> {
  try {
    const course = await courseService.getCourseById(courseId, { content: opts.content ?? 'published' });
    const sections = [...(course?.sections ?? [])].sort((a, b) => a.order - b.order);
    const lessons = sections.flatMap((s) =>
      [...(s.lessons ?? [])].sort((a, b) => a.order - b.order),
    ) as unknown as CourseLessonLike[];
    if (!lessons.length) return null;
    return splitCourseHorizon(lessons, currentLessonId);
  } catch {
    return null;
  }
}
